import { canCreateExpense } from '../auth/permissions';
import type { IconName } from '../components/ui/Icon';
import type { Tone } from '../components/ui/primitives/cx';
import { canCreateProduct, canOpenPos, canOpenSection, type AppSection } from '../layouts/navConfig';
import type { UserRole } from '../types/auth';

export interface QuickAccessItem {
  id: string;
  icon: IconName;
  title: string;
  /** Solo el acceso principal lleva una línea de apoyo (P-030). */
  text?: string;
  /** A dónde lleva: un destino del menú, o una ruta fuera del menú con su propio guard («Nuevo producto»). */
  destination: AppSection;
  /** El acceso destacado: «Vender». */
  primary: boolean;
  /** El color del mosaico de su icono. */
  tone: Tone | 'brand';
}

interface FixedAccess extends QuickAccessItem {
  /** Lo que ya hace falta hoy para usar el destino. No concede nada: solo decide si se dibuja. */
  allowed: (role: UserRole | undefined) => boolean;
}

/**
 * Los accesos rápidos del Inicio (P-030), FIJOS por decisión del dueño (B0-05): Vender, Registrar
 * compra, Registrar gasto, Inventario y Nuevo producto. Sin favoritos, sin reordenar y sin
 * personalizar (FUTURE_UPGRADES #34). B0-05 supersede el tope de tres accesos de Owner Intelligence
 * Entrega 1 y sus ternas por rol.
 *
 * Rediseño · Bloque E: «Nuevo producto» llega con su pantalla, que tiene ruta propia
 * (`#nuevo-producto`, fuera del menú; N8 / FUTURE_UPGRADES #47). Hasta entonces no se dibujaba: un
 * acceso con ese nombre que abriera otra cosa habría sido peor que no tenerlo.
 *
 * Cada persona ve los accesos que YA puede usar, igual que con las pestañas de la barra inferior:
 * ninguno abre algo que su rol no pueda abrir (`canOpenSection`: un destino del menú que ve, o una
 * ruta fuera del menú cuyo guard pasa).
 *   · «Vender» va directo al punto de venta de siempre (la vista «Venta rápida» de Ventas Otros),
 *     con la misma regla que el destino «Vender» del menú (`canOpenPos`).
 *   · «Registrar gasto» pide, además de ver Gastos, poder capturar uno.
 *   · «Nuevo producto», con la misma regla que el guard de su ruta (`canCreateProduct`): OWNER y
 *     MANAGER. El color y el icono son los de P-030 (lila y «+»).
 */
const FIXED_ACCESSES: FixedAccess[] = [
  { id: 'sale', icon: 'sell', title: 'Vender', text: 'Abre el punto de venta', destination: 'vender', primary: true, tone: 'brand', allowed: canOpenPos },
  { id: 'purchase', icon: 'purchases', title: 'Registrar compra', destination: 'purchases', primary: false, tone: 'info', allowed: () => true },
  { id: 'expense', icon: 'expenses', title: 'Registrar gasto', destination: 'expenses', primary: false, tone: 'danger', allowed: canCreateExpense },
  { id: 'inventory', icon: 'inventory', title: 'Inventario', destination: 'inventory', primary: false, tone: 'warning', allowed: () => true },
  { id: 'product', icon: 'plus', title: 'Nuevo producto', destination: 'nuevo-producto', primary: false, tone: 'accent', allowed: canCreateProduct },
];

export function quickAccessItems(role: UserRole | undefined): QuickAccessItem[] {
  return FIXED_ACCESSES
    .filter((access) => canOpenSection(role, access.destination) && access.allowed(role))
    .map(({ allowed: _allowed, ...item }) => item);
}
